/** Voice Template Generation — Canonical Generation Card (Result-first).
 *
 * VISTOERA_VOICE_TEMPLATE_GENERATION_CANONICAL_GENERATION_CARD_RESULT_FIRST_V2
 *
 * HARD vertical order:
 * HEADER → RESULT → VOICE SOURCE → PARAMETERS → TEMPLATE NAME → VOICE DESCRIPTION → FOOTER
 *
 * Collapsed (default): HEADER + RESULT only.
 * Expand via CanvasBoard `expandedNodeId` (single expanded Generation Card).
 * External Quick Actions: CARD RIGHT OUTSIDE via VoiceTemplateWorkflowRail + Result-zone hover bridge.
 *
 * Backend contract unchanged: source_type + voice_brief + preview_text + template_name + refs.
 * Language/Gender/Style/Scene are Skill semantic params mapped into voice_brief (not fake MiniMax API fields).
 * Preview text lives under More Parameters (backend still requires it).
 * Template name is artifact display name — rename does not mark stale.
 * Diverge not rendered.
 */

import {
  CloseOutlined,
  CopyOutlined,
  DownloadOutlined,
  FolderOpenOutlined,
  HistoryOutlined,
  LoadingOutlined,
  SoundOutlined,
  ThunderboltOutlined,
  UserOutlined,
} from '@ant-design/icons'
import { Handle, Position, useUpdateNodeInternals, type NodeProps } from '@xyflow/react'
import { Checkbox, Input, Tooltip, message, type InputRef } from 'antd'
import CanvasSelect from '../CanvasSelect'
import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
} from 'react'
import { useTranslation } from 'react-i18next'
import {
  canvasVoiceTemplateExportUrl,
  generateCanvasVoiceTemplate,
  getCanvasVoiceTemplate,
  getCanvasVoiceTemplateRuntimeCapabilities,
  uploadCanvasVoiceTemplateReferenceAudio,
  type SaasApiError,
} from '../../../../api'
import CanvasActionPopover from '../CanvasActionPopover'
import { useCanvasImeSafeTextHandlers } from '../canvasEditableKeyboardGuard'
import { patchCanvasFieldsIfChanged } from '../canvasFieldsEqual'
import { downloadCanvasMedia } from '../canvasDownload'
import { mediaPreviewHandleTopPx } from '../canvasNodeHandleAnchor'
import {
  buildExplicitProviderSelectionPatch,
  findProviderOptionById,
  isProviderOptionSelectable,
  normalizeProviderSelectionMode,
  resolveCapabilityProviderOptionId,
} from '../capabilityProviderSelection'
import { GENERIC_INPUT_HANDLE } from '../dataflow'
import { functionCardShowsInspector } from '../functionCardInteractionV2'
import { NODE_EXPAND_TRIGGER_CLASS, canvasNodeShellStateClass } from '../canvasNodeShellV2'
import CanvasFunctionCardHeader from '../CanvasFunctionCardHeader'
import { useNodeDefinition } from '../nodeRegistry'
import { useCanvasNodeRegistryI18n } from '../nodeRegistryI18n'
import { useCanvasGenerationI18n } from '../useCanvasGenerationI18n'
import { useCapabilityProviderOptions } from '../useCapabilityProviderOptions'
import VoiceAudioPlayer from '../../../virtualIp/VoiceAudioPlayer'
import { audioAssetPickerInitialSelectionFromReferenceAssetId } from '../../../../components/AudioAssetPicker/audioAssetPickerContract'
import VoiceReferenceCapturePanel, { type VoiceReferenceCaptureValue } from '../components/VoiceReferenceCapturePanel'
import { type VoiceReferenceRecordingDraftStatus } from '../../../../voiceReference/voiceReferenceRecordingDraftStore'
import VoiceTemplateWorkflowRail from './VoiceTemplateWorkflowRail'
import { formatCanvasVoiceTemplateError } from '../voiceTemplateError'
import {
  CANONICAL_TEMPLATE_NAME_LIMIT,
  type VoiceTemplateFieldErrors,
} from '../voiceTemplateValidation'
import {
  evaluateVoiceTemplateGenerationReadiness,
  getVoiceTemplateVisiblePrimaryParams,
} from '../voiceTemplateGenerationReadiness'
import {
  VOICE_TEMPLATE_CAPABILITY,
  VOICE_TEMPLATE_INSPIRATION_EN,
  VOICE_TEMPLATE_INSPIRATION_ZH,
  VOICE_TEMPLATE_SKILL_KEY,
  VOICE_TEMPLATE_SKILL_VERSION,
  appendVoiceTemplateGenerationHistoryEntry,
  buildVoiceTemplateGeneratePayload,
  canvasVoiceTemplatePlaybackUrl,
  mapVoiceTemplateDtoToFields,
  type VoiceTemplateGender,
  type VoiceTemplateGenerationHistoryEntry,
  type VoiceTemplateLanguage,
  type VoiceTemplateScene,
  type VoiceTemplateSourceType,
  type VoiceTemplateStyle,
} from '../voiceTemplateProvider'
import type { FlowNodeData } from '../types'

import { generationNodeCardWidth } from '../generationNodeWidthPresets'

const NODE_CARD_WIDTH = generationNodeCardWidth('generate_voice_template')
const RESULT_AREA_HEIGHT = 148
const DEFAULT_PREVIEW_TEXT_ZH = '你好，欢迎使用 Vistoera，这是一个专注于 AI 内容创作的平台。'
const DEFAULT_PREVIEW_TEXT_EN = 'Hello, welcome to Vistoera, a platform focused on AI content creation.'

function GenerateVoiceTemplateNodeImpl({ id, data, selected }: NodeProps) {
  const { t, i18n } = useTranslation('canvas')
  const { mediaHeader } = useCanvasGenerationI18n()
  const { nodeLabel } = useCanvasNodeRegistryI18n()
  const nodeData = data as unknown as FlowNodeData & {
    workspaceId?: string
    projectId?: string
    onQuickCreateGenerateAudio?: (sourceNodeId: string) => void
    onConfirmCandidateVoiceTemplate?: (voiceTemplateNodeId: string, generateAudioNodeId: string) => void
    onDuplicateForRegenerate?: (sourceNodeId: string) => void
    onOpenAudioAssetPicker?: FlowNodeData['onOpenAudioAssetPicker']
  }
  const def = useNodeDefinition(nodeData.nodeType)
  const displayLabel = nodeLabel(nodeData.nodeType, def.label) || t('voiceTemplate.title')
  const workspaceId = nodeData.workspaceId || ''
  const projectId = nodeData.projectId || ''
  const fields = nodeData.fields || {}

  const isDetailExpanded = Boolean(nodeData.isDetailExpanded)
  const showExpanded = functionCardShowsInspector(isDetailExpanded, nodeData.isDragTempCollapsed)

  const sourceType = (fields.sourceType as VoiceTemplateSourceType | undefined) ?? 'AI_GENERATED'
  const voiceDescription = (fields.voiceDescription as string | undefined) ?? ''
  const previewText = (fields.previewText as string | undefined) ?? ''
  const templateName = (fields.templateName as string | undefined) ?? ''
  const language = (fields.language as VoiceTemplateLanguage | undefined) ?? 'zh-CN'
  const gender = (fields.gender as VoiceTemplateGender | undefined) ?? 'any'
  const style = (fields.style as VoiceTemplateStyle | undefined) ?? 'natural'
  const scene = (fields.scene as VoiceTemplateScene | undefined) ?? 'general'
  const referenceAssetId = (fields.referenceAssetId as string | null | undefined) ?? null
  const referenceFilename = (fields.referenceFilename as string | null | undefined) ?? null
  const referenceDurationMs = (fields.referenceDurationMs as number | null | undefined) ?? null
  const referenceCaptureMethod =
    (fields.referenceCaptureMethod as 'UPLOAD' | 'MICROPHONE' | 'LIBRARY' | null | undefined) ?? null
  const referencePlaybackUrl = (fields.referencePlaybackUrl as string | null | undefined) ?? null
  const voiceRightsConfirmed = Boolean(fields.voiceRightsConfirmed)
  const status = (fields.status as string | undefined) ?? 'READY'
  const templateId = (fields.templateId as string | null | undefined) ?? null
  const previewAudioAssetId = (fields.previewAudioAssetId as string | null | undefined) ?? null
  const previousPreviewAudioAssetId = (fields.previousPreviewAudioAssetId as string | null | undefined) ?? null
  const errorMessage = (fields.errorMessage as string | null | undefined) ?? null
  const needsRegeneration = Boolean(fields.needsRegeneration)
  const candidateForGenerateAudioNodeId =
    (fields.candidateForGenerateAudioNodeId as string | null | undefined) ?? null
  const generationHistory = (fields.generationHistory as VoiceTemplateGenerationHistoryEntry[] | undefined) ?? []
  const skillSelectionSource = (fields.skillSelectionSource as string | undefined) ?? 'AUTO'
  const providerSelectionMode = normalizeProviderSelectionMode(fields.providerSelectionMode)
  const storedProviderOptionId = (fields.providerOptionId as string | null | undefined) ?? null

  const [fieldErrors, setFieldErrors] = useState<VoiceTemplateFieldErrors>({})
  const [referenceRecordingDraftStatus, setReferenceRecordingDraftStatus] =
    useState<VoiceReferenceRecordingDraftStatus>('idle')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<SaasApiError | null>(null)
  const [voiceCloneRuntimeEnabled, setVoiceCloneRuntimeEnabled] = useState<boolean | null>(null)
  const [voiceCloneBlockerCode, setVoiceCloneBlockerCode] = useState<string | null>(null)
  const [regenerateChoiceOpen, setRegenerateChoiceOpen] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [moreParamsOpen, setMoreParamsOpen] = useState(false)
  const [hydrating, setHydrating] = useState(() => Boolean(templateId))
  const [resultZoneActive, setResultZoneActive] = useState(false)
  const resultZoneLeaveTimer = useRef<number | null>(null)
  const runBtnRef = useRef<HTMLButtonElement>(null)
  const historyBtnRef = useRef<HTMLButtonElement | null>(null)
  const [historyBtnEl, setHistoryBtnEl] = useState<HTMLButtonElement | null>(null)
  const templateNameInputRef = useRef<InputRef>(null)
  const updateNodeInternals = useUpdateNodeInternals()

  const { options: providerOptions, error: providerOptionsError } = useCapabilityProviderOptions(
    workspaceId,
    VOICE_TEMPLATE_CAPABILITY,
    'generate_voice_template',
  )

  const resolvedProviderOptionId = useMemo(
    () =>
      resolveCapabilityProviderOptionId({
        selectionMode: providerSelectionMode,
        storedProviderOptionId,
        options: providerOptions,
      }),
    [providerOptions, providerSelectionMode, storedProviderOptionId],
  )

  const providerSelectOptions = useMemo(
    () =>
      providerOptions.map((opt) => ({
        value: opt.provider_option_id,
        label: opt.frontend_display_name,
        title: opt.frontend_description || opt.frontend_display_name,
        disabled: !isProviderOptionSelectable(opt),
      })),
    [providerOptions],
  )

  const isBusy = isSubmitting || hydrating || status === 'GENERATING'
  const hasResult = status === 'COMPLETED' && !!previewAudioAssetId
  const displayPreviewAssetId =
    previewAudioAssetId || (isBusy || needsRegeneration ? previousPreviewAudioAssetId : null) || null
  const showActivePreview = Boolean(displayPreviewAssetId)
  const playbackUrl = displayPreviewAssetId
    ? canvasVoiceTemplatePlaybackUrl(workspaceId, displayPreviewAssetId)
    : ''
  const submitErrorMessage = submitError ? formatCanvasVoiceTemplateError(submitError, t) : null
  const visibleGenerationError = submitErrorMessage || errorMessage
  const showGenerationErrorBanner = Boolean(
    visibleGenerationError && (submitError || status === 'FAILED' || needsRegeneration),
  )

  const cardWidth = NODE_CARD_WIDTH
  const handleTop = mediaPreviewHandleTopPx(RESULT_AREA_HEIGHT)

  const patchFields = useCallback(
    (patch: Record<string, unknown>) => {
      const next = patchCanvasFieldsIfChanged(fields as Record<string, unknown>, patch)
      if (!next) return
      nodeData.onChangeFields?.(id, next)
    },
    [fields, id, nodeData],
  )

  const markStaleIfHasResult = useCallback(
    (patch: Record<string, unknown>) => {
      if (!hasResult && !previewAudioAssetId) return patch
      return {
        ...patch,
        needsRegeneration: true,
        previousPreviewAudioAssetId: previewAudioAssetId || previousPreviewAudioAssetId,
      }
    },
    [hasResult, previewAudioAssetId, previousPreviewAudioAssetId],
  )

  const { inputProps: templateNameInputProps, setValue: setTemplateNameValue } = useCanvasImeSafeTextHandlers({
    value: templateName,
    onValueChange: (next) => {
      patchFields({
        templateName: next,
        ...(status === 'FAILED' ? { status: 'READY', errorMessage: null } : {}),
      })
    },
    onAfterValueChange: () => {
      if (fieldErrors.templateName) {
        setFieldErrors((prev) => ({ ...prev, templateName: undefined }))
      }
    },
  })

  const { inputProps: voiceDescriptionInputProps, setValue: setVoiceDescriptionValue } = useCanvasImeSafeTextHandlers({
    value: voiceDescription,
    onValueChange: (next) => {
      patchFields(markStaleIfHasResult({ voiceDescription: next }))
    },
    onAfterValueChange: () => {
      if (fieldErrors.voiceDescription) {
        setFieldErrors((prev) => ({ ...prev, voiceDescription: undefined }))
      }
    },
  })

  const { inputProps: previewTextInputProps } = useCanvasImeSafeTextHandlers({
    value: previewText,
    onValueChange: (next) => {
      patchFields(markStaleIfHasResult({ previewText: next }))
    },
    onAfterValueChange: () => {
      if (fieldErrors.previewText) {
        setFieldErrors((prev) => ({ ...prev, previewText: undefined }))
      }
    },
  })

  const effectivePreviewText =
    previewText.trim() || (language === 'en-US' ? DEFAULT_PREVIEW_TEXT_EN : DEFAULT_PREVIEW_TEXT_ZH)

  const generationReadiness = useMemo(
    () =>
      evaluateVoiceTemplateGenerationReadiness(
        {
          workspaceId,
          projectId,
          sourceType,
          voiceDescription,
          previewText: effectivePreviewText,
          templateName,
          referenceAssetId: sourceType === 'AI_GENERATED' ? null : referenceAssetId,
          voiceRightsConfirmed,
          draftStatus: referenceRecordingDraftStatus,
          providerAvailable: providerSelectOptions.length > 0 && !providerOptionsError,
          voiceCloneRuntimeEnabled,
          voiceCloneBlockerCode,
        },
        t,
      ),
    [
      effectivePreviewText,
      projectId,
      providerOptionsError,
      providerSelectOptions.length,
      referenceAssetId,
      referenceRecordingDraftStatus,
      sourceType,
      t,
      templateName,
      voiceCloneRuntimeEnabled,
      voiceCloneBlockerCode,
      voiceDescription,
      voiceRightsConfirmed,
      workspaceId,
    ],
  )

  useEffect(() => {
    if (!workspaceId || !projectId || !showExpanded) return
    let cancelled = false
    getCanvasVoiceTemplateRuntimeCapabilities(workspaceId, projectId, id)
      .then((res) => {
        if (!cancelled) {
          setVoiceCloneRuntimeEnabled(res.data.voice_clone_runtime_enabled)
          setVoiceCloneBlockerCode(res.data.voice_clone_blocker_code ?? null)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setVoiceCloneRuntimeEnabled(null)
          setVoiceCloneBlockerCode(null)
        }
      })
    return () => {
      cancelled = true
    }
  }, [workspaceId, projectId, id, showExpanded])

  const visiblePrimaryParams = useMemo(
    () => getVoiceTemplateVisiblePrimaryParams(sourceType),
    [sourceType],
  )

  const activateResultZone = useCallback(() => {
    if (resultZoneLeaveTimer.current != null) {
      window.clearTimeout(resultZoneLeaveTimer.current)
      resultZoneLeaveTimer.current = null
    }
    setResultZoneActive(true)
  }, [])

  const deactivateResultZone = useCallback(() => {
    if (resultZoneLeaveTimer.current != null) window.clearTimeout(resultZoneLeaveTimer.current)
    resultZoneLeaveTimer.current = window.setTimeout(() => setResultZoneActive(false), 120)
  }, [])

  useEffect(() => {
    return () => {
      if (resultZoneLeaveTimer.current != null) window.clearTimeout(resultZoneLeaveTimer.current)
    }
  }, [])

  useEffect(() => {
    // VistoeraTASK-00187 forensic finding — a real, backend-SUCCEEDED
    // generation can end up with this node's OWN persisted `fields`
    // (templateId/status/previewAudioAssetId) never reflecting it (e.g. a
    // client-side request timeout/drop while a long-running real voice
    // clone kept executing and completed server-side afterwards). The
    // canonical source of truth for "does this node have a result" is the
    // backend `CanvasVoiceTemplate` row keyed by `canvas_node_id` — NOT
    // this component's own local `fields.templateId` pointer, which can
    // silently go stale. The GET .../voice-template endpoint already
    // resolves by node id alone (most-recently-updated row) when
    // `template_id` is omitted (see `_get_owned_template` — no backend
    // change needed here), so this effect now always attempts one
    // hydrate-by-node-id lookup on mount even when `templateId` is not
    // yet known locally, and self-heals the local fields if the backend
    // actually has a result. A node that has genuinely never generated
    // anything simply gets a 404 here, silently ignored exactly as
    // before — this never fabricates a fake success state, it only ever
    // reflects a REAL canonical artifact that already exists.
    if (!workspaceId || !projectId || isSubmitting) {
      if (!templateId) setHydrating(false)
      return
    }
    let cancelled = false
    getCanvasVoiceTemplate(workspaceId, projectId, id, templateId || undefined)
      .then((res) => {
        if (cancelled) return
        const mapped = mapVoiceTemplateDtoToFields(res.data, {
          interruptedGenerationMessage: t('voiceTemplate.errors.generationFailed'),
          formatErrorMessage: (err) => formatCanvasVoiceTemplateError(err, t),
        })
        patchFields(mapped)
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setHydrating(false)
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId, workspaceId, projectId, id])

  useEffect(() => {
    if (status === 'GENERATING' && !templateId && !isSubmitting) {
      patchFields({ status: 'READY', errorMessage: null })
    }
  }, [status, templateId, isSubmitting, patchFields])

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => updateNodeInternals(id))
    return () => window.cancelAnimationFrame(frame)
  }, [id, showExpanded, moreParamsOpen, sourceType, updateNodeInternals])

  const uploadReference = useCallback(
    async (file: File, durationMs: number, captureMethod: 'UPLOAD' | 'MICROPHONE') => {
      if (!workspaceId || !projectId) throw new Error('missing workspace')
      const res = await uploadCanvasVoiceTemplateReferenceAudio(
        workspaceId,
        projectId,
        id,
        file,
        durationMs,
        captureMethod,
      )
      return {
        asset_id: res.data.asset_id,
        filename: res.data.filename || file.name,
        duration_ms: res.data.duration_ms ?? durationMs,
        playback_url: res.data.playback_url,
      }
    },
    [workspaceId, projectId, id],
  )

  const openReferenceAudioPicker = useCallback(() => {
    if (isBusy || !nodeData.onOpenAudioAssetPicker) return
    nodeData.onOpenAudioAssetPicker({
      nodeId: id,
      binding: 'reference_audio',
      title: t('voiceTemplate.referenceCapture.pickerTitle'),
      allowedCategories: ['REFERENCE_AUDIO', 'RECORDING'],
      initialCategory: 'REFERENCE_AUDIO',
      presentationType: 'REFERENCE_AUDIO',
      lockPresentationType: false,
      initialSelection: audioAssetPickerInitialSelectionFromReferenceAssetId(referenceAssetId),
    })
  }, [id, isBusy, nodeData, referenceAssetId, t])

  const handleReferenceConfirmed = useCallback(
    (value: VoiceReferenceCaptureValue) => {
      patchFields(
        markStaleIfHasResult({
          referenceAssetId: value.assetId,
          referenceFilename: value.filename,
          referenceDurationMs: value.durationMs,
          referenceCaptureMethod: value.captureMethod,
          referencePlaybackUrl: value.playbackUrl ?? null,
        }),
      )
      if (fieldErrors.referenceAssetId) {
        setFieldErrors((prev) => ({ ...prev, referenceAssetId: undefined }))
      }
    },
    [fieldErrors.referenceAssetId, markStaleIfHasResult, patchFields],
  )

  const handleReferenceCleared = useCallback(() => {
    patchFields(
      markStaleIfHasResult({
        referenceAssetId: null,
        referenceFilename: null,
        referenceDurationMs: null,
        referenceCaptureMethod: null,
        referencePlaybackUrl: null,
      }),
    )
  }, [markStaleIfHasResult, patchFields])

  const runGenerate = useCallback(async () => {
    if (isSubmitting) return
    const readiness = evaluateVoiceTemplateGenerationReadiness(
      {
        workspaceId,
        projectId,
        sourceType,
        voiceDescription,
        previewText: effectivePreviewText,
        templateName,
        referenceAssetId: sourceType === 'AI_GENERATED' ? null : referenceAssetId,
        voiceRightsConfirmed,
        draftStatus: referenceRecordingDraftStatus,
        providerAvailable: providerSelectOptions.length > 0 && !providerOptionsError,
        voiceCloneRuntimeEnabled,
      },
      t,
    )
    if (!readiness.ready) {
      setFieldErrors(readiness.fieldErrors)
      if (readiness.primaryBlocker?.message) {
        message.warning(readiness.primaryBlocker.message)
      }
      if (readiness.primaryBlocker?.field === 'templateName') {
        templateNameInputRef.current?.focus()
      }
      return
    }
    setFieldErrors({})
    setSubmitError(null)
    setIsSubmitting(true)
    if (hasResult && previewAudioAssetId) {
      const nextHistory = appendVoiceTemplateGenerationHistoryEntry(generationHistory, {
        version: generationHistory.length + 1,
        templateId,
        previewAudioAssetId,
        templateName,
        sourceType,
        voiceDescription,
        previewText: effectivePreviewText,
        language,
        gender,
        style,
        scene,
        referenceAssetId: sourceType === 'AI_GENERATED' ? null : referenceAssetId,
        providerOptionId: resolvedProviderOptionId,
        skillKey: skillSelectionSource === 'EXPLICIT' ? VOICE_TEMPLATE_SKILL_KEY : null,
        skillVersion: skillSelectionSource === 'EXPLICIT' ? VOICE_TEMPLATE_SKILL_VERSION : null,
        completedAt: new Date().toISOString(),
      })
      patchFields({
        status: 'GENERATING',
        errorMessage: null,
        previousPreviewAudioAssetId: previewAudioAssetId,
        generationHistory: nextHistory,
        needsRegeneration: false,
      })
    } else {
      patchFields({ status: 'GENERATING', errorMessage: null, needsRegeneration: false })
    }
    try {
      const payload = buildVoiceTemplateGeneratePayload({
        sourceType,
        voiceDescription,
        previewText: effectivePreviewText,
        templateName,
        referenceAssetId,
        voiceRightsConfirmed,
        language,
        gender,
        style,
        scene,
      })
      const res = await generateCanvasVoiceTemplate(workspaceId!, projectId!, id, payload)
      patchFields({
        status: 'COMPLETED',
        templateId: res.data.template_id,
        previewAudioAssetId: res.data.preview_audio_asset_id,
        providerVoiceId: res.data.provider_voice_id,
        provider: res.data.provider,
        executionAvailability: 'AVAILABLE',
        errorMessage: null,
        needsRegeneration: false,
        previewText: effectivePreviewText,
      })
    } catch (err) {
      const apiErr = err as SaasApiError
      const mapped = formatCanvasVoiceTemplateError(apiErr, t)
      setSubmitError(apiErr)
      message.error(mapped)
      patchFields({
        status: hasResult || previousPreviewAudioAssetId ? 'COMPLETED' : 'FAILED',
        previewAudioAssetId: previewAudioAssetId || previousPreviewAudioAssetId,
        errorMessage: mapped,
        needsRegeneration: Boolean(previewAudioAssetId || previousPreviewAudioAssetId),
      })
    } finally {
      setIsSubmitting(false)
    }
  }, [
    workspaceId,
    projectId,
    id,
    isSubmitting,
    sourceType,
    voiceDescription,
    effectivePreviewText,
    templateName,
    referenceAssetId,
    voiceRightsConfirmed,
    referenceRecordingDraftStatus,
    providerSelectOptions.length,
    providerOptionsError,
    voiceCloneRuntimeEnabled,
    language,
    gender,
    style,
    scene,
    t,
    hasResult,
    previewAudioAssetId,
    previousPreviewAudioAssetId,
    generationHistory,
    templateId,
    resolvedProviderOptionId,
    skillSelectionSource,
    patchFields,
  ])

  const handleRunClick = useCallback(() => {
    if (isBusy) return
    if (!generationReadiness.ready) {
      setFieldErrors(generationReadiness.fieldErrors)
      if (generationReadiness.primaryBlocker?.message) {
        message.warning(generationReadiness.primaryBlocker.message)
      }
      if (generationReadiness.primaryBlocker?.field === 'templateName') {
        templateNameInputRef.current?.focus()
      }
      return
    }
    if (hasResult) {
      setRegenerateChoiceOpen(true)
      return
    }
    void runGenerate()
  }, [generationReadiness, hasResult, isBusy, runGenerate])

  // VistoeraTASK-00219 — the node's top-right Header download/export action
  // must act on the VOICE TEMPLATE asset (the node's actual generated
  // artifact), never the few-second Preview Audio clip. Forensic finding:
  // MiniMax never exposes a portable "voice model file" (see
  // `canvas_voice_template_service.build_voice_template_export`'s own
  // docstring) — `provider_voice_id` only means anything when re-presented
  // to the same MiniMax account. This exports a Vistoera-native manifest
  // (never audio bytes) keyed by `templateId`, which is already kept in
  // sync with the CURRENT generation by both regenerate-in-place
  // (`runGenerate`'s `patchFields({ templateId: res.data.template_id, ... })`)
  // and History restore (`handleRestoreHistory`) — no new state needed.
  const handleExportTemplate = useCallback(
    (e: ReactMouseEvent) => {
      e.stopPropagation()
      if (!templateId) return
      downloadCanvasMedia(canvasVoiceTemplateExportUrl(workspaceId, projectId, id, templateId))
    },
    [templateId, workspaceId, projectId, id],
  )

  const handleCopyClick = (e: ReactMouseEvent) => {
    e.stopPropagation()
    nodeData.onDuplicateNode?.(id)
  }
  const handleDeleteClick = (e: ReactMouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    nodeData.onRequestDelete?.(id, e.currentTarget)
  }
  const handleQuickCreateGenerateAudio = useCallback(() => {
    nodeData.onQuickCreateGenerateAudio?.(id)
  }, [id, nodeData])

  const handleRestoreHistory = useCallback(
    (entry: VoiceTemplateGenerationHistoryEntry) => {
      setTemplateNameValue(entry.templateName)
      setVoiceDescriptionValue(entry.voiceDescription)
      patchFields({
        templateId: entry.templateId,
        previewAudioAssetId: entry.previewAudioAssetId,
        templateName: entry.templateName,
        sourceType: entry.sourceType,
        voiceDescription: entry.voiceDescription,
        previewText: entry.previewText,
        language: entry.language,
        gender: entry.gender,
        style: entry.style,
        scene: entry.scene,
        referenceAssetId: entry.referenceAssetId,
        status: entry.previewAudioAssetId ? 'COMPLETED' : 'READY',
        needsRegeneration: false,
        errorMessage: null,
      })
      setHistoryOpen(false)
    },
    [patchFields, setTemplateNameValue, setVoiceDescriptionValue],
  )

  const fillInspiration = useCallback(() => {
    const pool = language === 'en-US' || i18n.language.startsWith('en') ? VOICE_TEMPLATE_INSPIRATION_EN : VOICE_TEMPLATE_INSPIRATION_ZH
    const next = pool[Math.floor(Math.random() * pool.length)] || pool[0]
    setVoiceDescriptionValue(next)
  }, [i18n.language, language, setVoiceDescriptionValue])

  const resultStatusLabel = useMemo(() => {
    if (isBusy) return t('voiceTemplate.status.generating')
    if (needsRegeneration && showActivePreview) return t('generation.needsRegeneration')
    if (hasResult || showActivePreview) return t('voiceTemplate.status.ready')
    if (status === 'FAILED') return t('voiceTemplate.status.failed')
    return t('voiceTemplate.result.notGenerated')
  }, [hasResult, isBusy, needsRegeneration, showActivePreview, status, t])

  const runLabel = useMemo(() => {
    if (isBusy) return t('voiceTemplate.actions.generating')
    return hasResult ? t('voiceTemplate.actions.regenerate') : t('voiceTemplate.actions.generate')
  }, [hasResult, isBusy, t])

  const descriptionSectionTitle =
    sourceType === 'AI_GENERATED'
      ? t('voiceTemplate.sections.voiceDescriptionAi')
      : t('voiceTemplate.sections.voiceDescriptionOptional')

  const descriptionHint =
    sourceType === 'AI_GENERATED'
      ? t('voiceTemplate.hints.voiceDescriptionAi')
      : sourceType === 'REFERENCE'
        ? t('voiceTemplate.hints.voiceDescriptionReference')
        : t('voiceTemplate.hints.voiceDescriptionClone')

  const collapsedError = !showExpanded && (status === 'FAILED' || Object.keys(fieldErrors).length > 0)
  const selectedProviderLabel =
    findProviderOptionById(providerOptions, resolvedProviderOptionId)?.frontend_display_name ||
    t('voiceTemplate.provider.label')

  const sourceOptions: Array<{ value: VoiceTemplateSourceType; icon: React.ReactNode }> = [
    { value: 'AI_GENERATED', icon: <ThunderboltOutlined /> },
    { value: 'REFERENCE', icon: <FolderOpenOutlined /> },
    { value: 'CLONED', icon: <UserOutlined /> },
  ]

  return (
    <div
      className={`pcv-node pcv-node--media pcv-node--voice-generator pcv-node--generate-voice-template${selected ? ' is-selected' : ''} ${canvasNodeShellStateClass(showExpanded)}${resultZoneActive ? ' is-result-zone-active' : ''}`}
      style={
        {
          width: cardWidth,
          '--pcv-node-preview-height': `${RESULT_AREA_HEIGHT}px`,
        } as CSSProperties
      }
      data-testid="canvas-voice-template-node"
      data-node-id={id}
      data-expanded={showExpanded ? 'yes' : 'no'}
      data-canonical-order="Header|Result|VoiceSource|Parameters|TemplateName|VoiceDescription|Footer"
      data-template-name-before-voice-description="yes"
      data-result-first="yes"
    >
      <Handle
        type="target"
        position={Position.Left}
        id={GENERIC_INPUT_HANDLE}
        className="pcv-handle pcv-handle--lg"
        style={{ top: handleTop }}
      />
      <Handle
        type="source"
        position={Position.Right}
        id="voice-template-out"
        className="pcv-handle pcv-handle--lg"
        style={{ top: handleTop }}
      />

      <div className="pcv-node-inner">
        {/* ① Header */}
        <CanvasFunctionCardHeader
          className="pcv-gen-voice-template-header"
          icon={<SoundOutlined />}
          color={def.color}
          title={displayLabel}
          statusBadge={
            <>
              {needsRegeneration && showActivePreview && !isBusy ? (
                <span className="pcv-brief-header-badge pcv-gen-stale-badge" data-testid="voice-template-needs-regen-badge">
                  {t('generation.needsRegeneration')}
                </span>
              ) : null}
              {collapsedError ? (
                <span className="pcv-media-error-badge" title={errorMessage || t('voiceTemplate.status.failed')}>
                  {t('voiceTemplate.status.failedShort')}
                </span>
              ) : null}
            </>
          }
          actions={
            <>
              {showActivePreview ? (
                <Tooltip title={t('voiceTemplate.header.exportTemplate')} mouseEnterDelay={0.3}>
                  <button
                    type="button"
                    className="pcv-media-hover-btn"
                    data-testid="voice-template-header-download-btn"
                    aria-label={t('voiceTemplate.header.exportTemplate')}
                    onClick={handleExportTemplate}
                    disabled={!templateId}
                  >
                    <DownloadOutlined />
                  </button>
                </Tooltip>
              ) : null}
              <Tooltip title={t('voiceTemplate.history.title')} mouseEnterDelay={0.3}>
                <button
                  type="button"
                  ref={(el) => {
                    historyBtnRef.current = el
                    setHistoryBtnEl(el)
                  }}
                  className="pcv-media-hover-btn"
                  data-testid="voice-template-history-btn"
                  aria-label={t('voiceTemplate.history.title')}
                  onClick={(e) => {
                    e.stopPropagation()
                    setHistoryOpen(true)
                  }}
                >
                  <HistoryOutlined />
                </button>
              </Tooltip>
              <Tooltip title={mediaHeader.duplicate} mouseEnterDelay={0.3}>
                <button type="button" className="pcv-media-hover-btn" aria-label={mediaHeader.duplicate} onClick={handleCopyClick}>
                  <CopyOutlined />
                </button>
              </Tooltip>
              <Tooltip title={mediaHeader.delete} mouseEnterDelay={0.3}>
                <button
                  type="button"
                  className="pcv-media-hover-btn pcv-media-hover-btn--danger"
                  aria-label={mediaHeader.delete}
                  onClick={handleDeleteClick}
                >
                  <CloseOutlined />
                </button>
              </Tooltip>
            </>
          }
        />

        {/* ② Result — always visible */}
        <div
          className={`pcv-gen-result-area ${NODE_EXPAND_TRIGGER_CLASS}`}
          data-testid="voice-template-result-area"
          onMouseEnter={activateResultZone}
          onMouseLeave={deactivateResultZone}
          onClick={() => nodeData.onExpandFunctionCardDetail?.(id)}
        >
          {showActivePreview && playbackUrl ? null : (
            <div className="pcv-gen-result-section-label">
              <span>{t('voiceTemplate.result.title')}</span>
              <span className="pcv-gen-result-status-badge" data-testid="voice-template-result-status">
                {resultStatusLabel}
              </span>
            </div>
          )}
          {showActivePreview && playbackUrl ? (
            <div className="pcv-voice-template-result-player nodrag" onClick={(e) => e.stopPropagation()}>
              {/* VistoeraTASK-00219 — this is explicitly a short audition
                  clip, never the Voice Template asset itself; do not
                  relabel this "生成结果/已生成" (that implied the clip WAS
                  the final deliverable). */}
              <div className="pcv-voice-template-preview-caption" data-testid="voice-template-preview-caption">
                {t('voiceTemplate.result.previewCaption')}
              </div>
              <div className="pcv-voice-template-player-row">
                {templateName ? (
                  <Tooltip title={templateName} mouseEnterDelay={0.4}>
                    <div className="pcv-voice-template-result-name" title={templateName}>
                      {templateName}
                    </div>
                  </Tooltip>
                ) : null}
                <div className="pcv-voice-template-player-slot">
                  <VoiceAudioPlayer playbackUrl={playbackUrl} testId="canvas-voice-template-player" />
                </div>
              </div>
              {isBusy ? (
                <div className="pcv-gen-result-loading">
                  <LoadingOutlined /> {t('voiceTemplate.status.generating')}
                </div>
              ) : null}
            </div>
          ) : isBusy ? (
            <div className="pcv-gen-result-empty pcv-gen-result-loading">
              <LoadingOutlined />
              <span>{t('voiceTemplate.status.generating')}</span>
            </div>
          ) : showGenerationErrorBanner ? (
            <div className="pcv-gen-result-empty" data-testid="canvas-voice-template-generation-error">
              <span>{visibleGenerationError}</span>
              <button type="button" className="pcv-gen-inline-link" onClick={() => void runGenerate()}>
                {t('voiceTemplate.actions.retry')}
              </button>
            </div>
          ) : status === 'FAILED' && !showActivePreview ? (
            <div className="pcv-gen-result-empty">
              <span>{errorMessage || t('voiceTemplate.status.failed')}</span>
              <button type="button" className="pcv-gen-inline-link" onClick={() => void runGenerate()}>
                {t('voiceTemplate.actions.retry')}
              </button>
            </div>
          ) : (
            <div className="pcv-gen-result-empty" data-testid="voice-template-result-empty">
              <SoundOutlined className="pcv-media-pending-icon" />
              <span className="pcv-media-placeholder-hint">{t('voiceTemplate.result.emptyTitle')}</span>
              <span className="pcv-gen-header-subtitle">{t('voiceTemplate.result.emptyHint')}</span>
            </div>
          )}
        </div>

        {showExpanded ? (
          <div className="pcv-brief-body pcv-gen-input-body nodrag" onClick={(e) => e.stopPropagation()}>
            {/* ③ Voice Source */}
            <div className="pcv-brief-section" data-testid="voice-template-source-section">
              <div className="pcv-voice-section-label">{t('voiceTemplate.sections.selectSource')}</div>
              <div className="pcv-voice-section-hint">{t('voiceTemplate.hints.selectSource')}</div>
              <div className="pcv-voice-source-list pcv-voice-source-list--canonical">
                {sourceOptions.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className={`pcv-voice-source-btn${sourceType === opt.value ? ' is-active' : ''}`}
                    data-testid={`voice-template-source-${opt.value}`}
                    onClick={() => patchFields(markStaleIfHasResult({ sourceType: opt.value }))}
                    disabled={isBusy}
                  >
                    <span className="pcv-voice-source-icon">{opt.icon}</span>
                    <span className="pcv-voice-source-title">{t(`voiceTemplate.sources.${opt.value}.title`)}</span>
                    <span className="pcv-voice-source-desc">{t(`voiceTemplate.sources.${opt.value}.description`)}</span>
                  </button>
                ))}
              </div>
              {sourceType === 'REFERENCE' || sourceType === 'CLONED' ? (
                <VoiceReferenceCapturePanel
                  nodeId={id}
                  disabled={isBusy}
                  referenceAssetId={referenceAssetId}
                  referenceFilename={referenceFilename}
                  referenceDurationMs={referenceDurationMs}
                  referenceCaptureMethod={referenceCaptureMethod}
                  referencePlaybackUrl={referencePlaybackUrl}
                  onSelectFromLibrary={openReferenceAudioPicker}
                  onReferenceConfirmed={handleReferenceConfirmed}
                  onReferenceCleared={handleReferenceCleared}
                  onDraftStatusChange={setReferenceRecordingDraftStatus}
                  uploadReference={uploadReference}
                />
              ) : null}
              {sourceType === 'CLONED' ? (
                <Checkbox
                  checked={voiceRightsConfirmed}
                  data-testid="canvas-voice-template-rights-checkbox"
                  onChange={(e) => {
                    patchFields(markStaleIfHasResult({ voiceRightsConfirmed: e.target.checked }))
                    if (e.target.checked && fieldErrors.voiceRightsConfirmed) {
                      setFieldErrors((prev) => ({ ...prev, voiceRightsConfirmed: undefined }))
                    }
                  }}
                  disabled={isBusy}
                >
                  {t('voiceTemplate.clone.consent')}
                </Checkbox>
              ) : null}
              {fieldErrors.referenceAssetId ? (
                <div className="pcv-voice-field-error">{fieldErrors.referenceAssetId}</div>
              ) : null}
              {fieldErrors.voiceRightsConfirmed ? (
                <div className="pcv-voice-field-error">{fieldErrors.voiceRightsConfirmed}</div>
              ) : null}
            </div>

            {/* ④ Parameters — AI synthesis params only when source is AI_GENERATED */}
            {visiblePrimaryParams.length > 0 || moreParamsOpen ? (
              <div className="pcv-brief-section" data-testid="voice-template-params-section">
                <div className="pcv-gen-params-header">
                  <span>{t('voiceTemplate.sections.parameters')}</span>
                  <button
                    type="button"
                    className="pcv-gen-inline-link"
                    data-testid="voice-template-more-params"
                    onClick={() => setMoreParamsOpen((v) => !v)}
                  >
                    {t('voiceTemplate.actions.moreParameters')}
                  </button>
                </div>
                {visiblePrimaryParams.length > 0 ? (
                  <div className="pcv-gen-params-primary pcv-voice-template-params-grid">
                    {visiblePrimaryParams.includes('language') ? (
                      <label className="pcv-gen-field">
                        <span>{t('voiceTemplate.params.language')}</span>
                        <CanvasSelect
                          size="small"
                          value={language}
                          disabled={isBusy}
                          options={[
                            { value: 'zh-CN', label: t('voiceTemplate.params.languageZh') },
                            { value: 'en-US', label: t('voiceTemplate.params.languageEn') },
                          ]}
                          onChange={(v) => patchFields(markStaleIfHasResult({ language: v }))}
                        />
                      </label>
                    ) : null}
                    {visiblePrimaryParams.includes('gender') ? (
                      <label className="pcv-gen-field">
                        <span>{t('voiceTemplate.params.gender')}</span>
                        <CanvasSelect
                          size="small"
                          value={gender}
                          disabled={isBusy}
                          options={[
                            { value: 'any', label: t('voiceTemplate.params.genderAny') },
                            { value: 'male', label: t('voiceTemplate.params.genderMale') },
                            { value: 'female', label: t('voiceTemplate.params.genderFemale') },
                          ]}
                          onChange={(v) => patchFields(markStaleIfHasResult({ gender: v }))}
                        />
                      </label>
                    ) : null}
                    {visiblePrimaryParams.includes('style') ? (
                      <label className="pcv-gen-field">
                        <span>{t('voiceTemplate.params.style')}</span>
                        <CanvasSelect
                          size="small"
                          value={style}
                          disabled={isBusy}
                          options={[
                            { value: 'natural', label: t('voiceTemplate.params.styleNatural') },
                            { value: 'calm', label: t('voiceTemplate.params.styleCalm') },
                            { value: 'gentle', label: t('voiceTemplate.params.styleGentle') },
                            { value: 'lively', label: t('voiceTemplate.params.styleLively') },
                            { value: 'professional', label: t('voiceTemplate.params.styleProfessional') },
                            { value: 'narrative', label: t('voiceTemplate.params.styleNarrative') },
                          ]}
                          onChange={(v) => patchFields(markStaleIfHasResult({ style: v }))}
                        />
                      </label>
                    ) : null}
                    {visiblePrimaryParams.includes('scene') ? (
                      <label className="pcv-gen-field">
                        <span>{t('voiceTemplate.params.scene')}</span>
                        <CanvasSelect
                          size="small"
                          value={scene}
                          disabled={isBusy}
                          options={[
                            { value: 'general', label: t('voiceTemplate.params.sceneGeneral') },
                            { value: 'ad', label: t('voiceTemplate.params.sceneAd') },
                            { value: 'narration', label: t('voiceTemplate.params.sceneNarration') },
                            { value: 'audiobook', label: t('voiceTemplate.params.sceneAudiobook') },
                            { value: 'customer_service', label: t('voiceTemplate.params.sceneCustomerService') },
                            { value: 'character', label: t('voiceTemplate.params.sceneCharacter') },
                          ]}
                          onChange={(v) => patchFields(markStaleIfHasResult({ scene: v }))}
                        />
                      </label>
                    ) : null}
                  </div>
                ) : null}
                {moreParamsOpen ? (
                  <div className="pcv-voice-template-more-params" data-testid="voice-template-preview-text-section">
                    <div className="pcv-voice-section-label">{t('voiceTemplate.sections.previewText')}</div>
                    <div className="pcv-voice-section-hint">{t('voiceTemplate.hints.auditionText')}</div>
                    <Input.TextArea
                      className="pcv-gen-prompt nodrag nowheel"
                      {...previewTextInputProps}
                      maxLength={300}
                      rows={2}
                      showCount
                      placeholder={t('voiceTemplate.placeholders.previewText')}
                      disabled={isBusy}
                      data-testid="voice-template-preview-text-input"
                    />
                    {fieldErrors.previewText ? <div className="pcv-voice-field-error">{fieldErrors.previewText}</div> : null}
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="pcv-brief-section pcv-voice-template-more-params-entry" data-testid="voice-template-params-section">
                <button
                  type="button"
                  className="pcv-gen-inline-link"
                  data-testid="voice-template-more-params"
                  onClick={() => setMoreParamsOpen(true)}
                >
                  {t('voiceTemplate.actions.moreParameters')}
                </button>
              </div>
            )}

            {/* ⑤ Template Name — BEFORE Voice Description */}
            <div className="pcv-brief-section" data-testid="voice-template-name-section">
              <div className="pcv-voice-section-label">{t('voiceTemplate.sections.templateName')}</div>
              <div className="pcv-voice-section-hint">{t('voiceTemplate.hints.templateName')}</div>
              <Input
                ref={templateNameInputRef}
                className="pcv-gen-prompt nodrag nowheel"
                {...templateNameInputProps}
                maxLength={CANONICAL_TEMPLATE_NAME_LIMIT}
                showCount
                status={fieldErrors.templateName ? 'error' : undefined}
                placeholder={t('voiceTemplate.placeholders.templateName')}
                disabled={isBusy}
                data-testid="canvas-voice-template-name-input"
              />
              {fieldErrors.templateName ? (
                <div className="pcv-voice-field-error" data-testid="canvas-voice-template-name-error">
                  {fieldErrors.templateName}
                </div>
              ) : null}
            </div>

            {/* ⑥ Voice Description */}
            <div className="pcv-brief-section" data-testid="voice-template-description-section">
              <div className="pcv-voice-section-label">{descriptionSectionTitle}</div>
              <div className="pcv-voice-section-hint">{descriptionHint}</div>
              <Input.TextArea
                className="pcv-gen-prompt nodrag nowheel"
                {...voiceDescriptionInputProps}
                maxLength={500}
                rows={3}
                showCount
                placeholder={t('voiceTemplate.placeholders.voiceDescription')}
                disabled={isBusy}
                data-testid="voice-template-description-input"
              />
              <div className="pcv-voice-description-tools">
                <button type="button" className="pcv-gen-tool-btn" disabled={isBusy} onClick={fillInspiration}>
                  {t('voiceTemplate.actions.randomInspiration')}
                </button>
                <button
                  type="button"
                  className="pcv-gen-tool-btn"
                  disabled={isBusy || !voiceDescriptionInputProps.value}
                  onClick={() => setVoiceDescriptionValue('')}
                >
                  {t('voiceTemplate.actions.clearDescription')}
                </button>
              </div>
              {fieldErrors.voiceDescription ? <div className="pcv-voice-field-error">{fieldErrors.voiceDescription}</div> : null}
            </div>

            {showGenerationErrorBanner ? (
              <div className="pcv-gen-capability-warning pcv-gen-capability-warning--error" data-testid="canvas-voice-template-generation-error">
                {visibleGenerationError}
              </div>
            ) : null}

            {candidateForGenerateAudioNodeId ? (
              <button
                type="button"
                className="pcv-gen-tool-btn"
                onClick={() => nodeData.onConfirmCandidateVoiceTemplate?.(id, candidateForGenerateAudioNodeId)}
              >
                {t('voiceTemplate.actions.useAsCandidate', { defaultValue: 'Use this template' })}
              </button>
            ) : null}

            {/* ⑦ Footer */}
            {!generationReadiness.ready && generationReadiness.primaryBlocker ? (
              <div
                className="pcv-voice-field-error pcv-voice-generate-blocker"
                data-testid="voice-template-generate-blocker"
              >
                {generationReadiness.primaryBlocker.message}
              </div>
            ) : null}
            <div className="pcv-gen-footer nodrag" onClick={(e) => e.stopPropagation()} data-testid="voice-template-footer">
              <Tooltip title={providerOptionsError || selectedProviderLabel}>
                <CanvasSelect
                  className="pcv-gen-footer-select"
                  size="middle"
                  data-testid="voice-template-provider-select"
                  placeholder={t('voiceTemplate.provider.label')}
                  value={resolvedProviderOptionId ?? undefined}
                  options={providerSelectOptions}
                  onChange={(v) => patchFields(markStaleIfHasResult(buildExplicitProviderSelectionPatch(v)))}
                  disabled={isBusy || providerSelectOptions.length === 0}
                />
              </Tooltip>
              <CanvasSelect
                className="pcv-gen-footer-select"
                size="middle"
                data-testid="voice-template-skill-select"
                value={skillSelectionSource === 'EXPLICIT' ? VOICE_TEMPLATE_SKILL_KEY : 'AUTO'}
                options={[
                  { value: 'AUTO', label: t('voiceTemplate.skill.auto') },
                  { value: VOICE_TEMPLATE_SKILL_KEY, label: t('voiceTemplate.skill.v1') },
                ]}
                onChange={(v) =>
                  patchFields(
                    markStaleIfHasResult(
                      v === 'AUTO'
                        ? { skillSelectionSource: 'AUTO', skillKey: null, skillVersion: null }
                        : {
                            skillSelectionSource: 'EXPLICIT',
                            skillKey: VOICE_TEMPLATE_SKILL_KEY,
                            skillVersion: VOICE_TEMPLATE_SKILL_VERSION,
                          },
                    ),
                  )
                }
                disabled={isBusy}
              />
              <Tooltip
                title={
                  !generationReadiness.ready && generationReadiness.primaryBlocker
                    ? generationReadiness.primaryBlocker.message
                    : undefined
                }
              >
                <button
                  ref={runBtnRef}
                  type="button"
                  className="pcv-gen-footer-generate-btn"
                  data-testid="voice-template-generate-btn"
                  disabled={isBusy || !generationReadiness.ready}
                  data-ready={generationReadiness.ready ? 'yes' : 'no'}
                  onClick={handleRunClick}
                >
                  {isBusy ? <LoadingOutlined /> : <SoundOutlined />}
                  {runLabel}
                </button>
              </Tooltip>
            </div>
          </div>
        ) : null}
      </div>

      {showActivePreview && templateId && !needsRegeneration ? (
        <VoiceTemplateWorkflowRail
          onQuickCreateGenerateAudio={handleQuickCreateGenerateAudio}
          onMouseEnter={activateResultZone}
          onMouseLeave={deactivateResultZone}
        />
      ) : showActivePreview && templateId && needsRegeneration ? (
        <VoiceTemplateWorkflowRail
          disabled
          disabledReason={t('generation.needsRegeneration')}
          onQuickCreateGenerateAudio={handleQuickCreateGenerateAudio}
          onMouseEnter={activateResultZone}
          onMouseLeave={deactivateResultZone}
        />
      ) : null}

      <CanvasActionPopover
        anchorEl={historyBtnEl}
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        title={t('voiceTemplate.history.title')}
      >
        {generationHistory.length === 0 ? (
          <p className="pcv-action-popover-desc">{t('voiceTemplate.history.empty')}</p>
        ) : (
          <div className="pcv-gen-history-list">
            {[...generationHistory].reverse().map((entry) => (
              <button
                key={`${entry.version}-${entry.completedAt}`}
                type="button"
                className="pcv-gen-history-item"
                onClick={() => handleRestoreHistory(entry)}
              >
                <span>
                  v{entry.version} · {entry.templateName || t('voiceTemplate.result.title')}
                </span>
                <span className="pcv-gen-header-subtitle">{new Date(entry.completedAt).toLocaleString()}</span>
              </button>
            ))}
          </div>
        )}
      </CanvasActionPopover>

      <CanvasActionPopover
        anchorEl={runBtnRef.current}
        open={regenerateChoiceOpen}
        onClose={() => setRegenerateChoiceOpen(false)}
        title={t('voiceTemplate.regenerate.title')}
      >
        <p className="pcv-action-popover-desc">{t('voiceTemplate.regenerate.description')}</p>
        <div className="pcv-action-popover-actions">
          <button
            type="button"
            className="pcv-action-popover-btn"
            onClick={() => {
              setRegenerateChoiceOpen(false)
              nodeData.onDuplicateForRegenerate?.(id)
            }}
          >
            {t('voiceTemplate.regenerate.separate')}
          </button>
          <button
            type="button"
            className="pcv-action-popover-btn pcv-action-popover-btn--primary"
            onClick={() => {
              setRegenerateChoiceOpen(false)
              void runGenerate()
            }}
          >
            {t('voiceTemplate.regenerate.overwrite')}
          </button>
        </div>
      </CanvasActionPopover>
    </div>
  )
}

const GenerateVoiceTemplateNode = memo(GenerateVoiceTemplateNodeImpl)
export default GenerateVoiceTemplateNode
